/**
 * ring-swap admin Generation History client + gating helpers.
 *
 * Mirrors the `api/reviews.ts` pattern (authFetch, `/ring-swap` prefix, throw on !ok). Wire shapes
 * are the API's snake_case §2 contract; `request_json` is the original `GenerationRequest`, so the
 * History drawer can re-run a generation by POSTing it back verbatim.
 */
import type { AuthService } from "../auth/auth-service";
import type { GenerationRequest } from "./ringswap";
import { config } from "../config";

const base = config.ringSwapPrefix; // e.g. "/ring-swap" on the gateway

/** A stone as summarised on a history row (snake_case wire). */
export interface HistoryStoneWire {
  type: string;
  shape: string;
  carat_band: string;
  reference_image_url: string | null;
}

/** One generation as returned by GET …/admin/generations/history (snake_case wire). */
export interface HistoryItemWire {
  id: string;
  created_at: string;
  completed_at: string | null;
  status: string;
  qa_status: string | null;
  qa_score: number | null;
  operation: string;
  target_metal: string | null;
  ring_sku: string;
  ring_name: string;
  source_image_url: string;
  output_image_url: string | null;
  stone: HistoryStoneWire | null;
  model: string | null;
  model_version: string | null;
  generation_config_version: number;
  cost_micros: number | null;
  attempt_count: number;
  reviewed_by: string | null;
  /** The original request body — non-null makes the row replayable (open-in-bench / re-run). */
  request_json: GenerationRequest | null;
}

/** A page of history plus the opaque cursor for the next page (null at the end). */
export interface HistoryPageWire {
  items: HistoryItemWire[];
  next_cursor: string | null;
}

export interface HistoryQueryOptions {
  limit?: number;
  cursor?: string;
  status?: string;
  operation?: string;
  q?: string;
}

const DEFAULT_LIMIT = 24;

/**
 * Build the history querystring. Default limit 24; every filter is omitted when absent (each
 * optional is read explicitly so `exactOptionalPropertyTypes` never lets an `undefined` become the
 * literal string "undefined" in the URL).
 */
export function historyQueryString(opts: HistoryQueryOptions): string {
  const params = new URLSearchParams();
  params.set("limit", String(opts.limit ?? DEFAULT_LIMIT));
  if (opts.cursor !== undefined) params.set("cursor", opts.cursor);
  if (opts.status !== undefined) params.set("status", opts.status);
  if (opts.operation !== undefined) params.set("operation", opts.operation);
  if (opts.q !== undefined) params.set("q", opts.q);
  return params.toString();
}

/** Fetch one page of generation history. Throws on a non-2xx (mirrors reviews.ts). */
export async function fetchHistoryPage(service: AuthService, opts: HistoryQueryOptions): Promise<HistoryPageWire> {
  const res = await service.authFetch(`${base}/v1/admin/generations/history?${historyQueryString(opts)}`);
  if (!res.ok) throw new Error(`history load failed: HTTP ${res.status}`);
  return (await res.json()) as HistoryPageWire;
}

// ── Pure row-gating helpers (drive which actions a history row offers) ───────────────────────────

/** Open-in-bench needs a replayable request body. */
export function canOpenInBench(item: HistoryItemWire): boolean {
  return item.request_json !== null;
}

/** Re-run needs a replayable request body (the same predicate, named for its call site). */
export function canRerun(item: HistoryItemWire): boolean {
  return item.request_json !== null;
}

/** Review is offered only for a completed generation still awaiting QA. */
export function canReview(item: HistoryItemWire): boolean {
  return item.status === "complete" && item.qa_status === "needs_review";
}

/** Render a `cost_micros` (millionths of GBP) as pounds, or an em dash when unknown. */
export function costLabel(micros: number | null): string {
  if (micros === null) return "—";
  return `£${(micros / 1e6).toFixed(2)}`;
}
